"use client";
import React from "react";
import Head from "next/head";
import { TurnrCard, TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import styles from "./index.module.scss";
import { fetchEntitiesPaginated, fetchEntityActions } from "~/services";
import { useTablePageState, useTableSizeState } from "~/hooks";
import { useTableSortQuery } from "~/components/table/useTableSortQuery.jsx";
import { getTableHeadDateTime } from "~/components/table/cells/TableCellDateTime.jsx";
import { getTableHeadBrand } from "~/components/table/cells/TableCellBrand.jsx";
import TurnrTableSmart, { TurnrTableCell, TurnrTableRow } from "~/components/table/TurnrTableSmart.jsx";
import { getWebhookNotificationsPageData } from "~/cores/pageAccessCore";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { getTableHeadActions, TableCellActions } from "~/components/table/cells/TableCellActions.jsx";
import { ENTITY_TYPE } from "~/constants";
import { extractCreateEntityAction } from "~/components/entityAction/utils/entityActionsCore.js";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import EntityActionButton from "~/components/entityAction/EntityActionButton.jsx";
import { formatReadableDateAndTime, useServiceHook } from "rylak-common";
import { useRunCallbackIfValueDeeplyChanged } from "rylak-common/src/hooks/useRunCallbackIfValueDeeplyChanged.jsx";
import { useAppProps } from "~/app/_apps/AppPropsContext";
import ExternalLink from "~/components/ExternalLink.jsx";

function getTableHeadData({ appProps }) {
    return {
        items: [
            getTableHeadBrand({ id: "order.merchantBrand.name", appProps }),
            {
                id: "type",
                label: "Type",
            },
            {
                id: "status",
                label: "Status",
            },
            getTableHeadDateTime({ label: "Time" }),
            getTableHeadActions(),
        ].filter((v) => v),
    };
}

export default function WebhookOutgoingSubscriptionPage() {
    const { appProps } = useAppProps();
    const { userData, selectedBrandName, setSelectedBrandId } = appProps;

    const [page, setPage] = useTablePageState();
    const [size] = useTableSizeState();
    const tableHeadData = getTableHeadData({ appProps });
    let tableSortData = useTableSortQuery(tableHeadData);
    const { sort } = tableSortData;

    let { serviceData: webhookSubscriptionActions, fetchServiceData: fetchWebhookSubscriptionsActionsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntityActions,
    });

    let { serviceData: webhookSubscriptions, fetchServiceData: fetchWebhookSubscriptionsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntitiesPaginated,
    });

    let triggerWebhookSubscriptionsFetch = () => {
        fetchWebhookSubscriptionsFn(
            {
                entityType: ENTITY_TYPE.WEBHOOK_SUBSCRIPTION,
                page,
                size,
                sort,
            },
            { silentFetch: true },
        );
    };
    let triggerWebhookSubscriptionsActionsFetch = () => {
        fetchWebhookSubscriptionsActionsFn(
            {
                entityType: ENTITY_TYPE.WEBHOOK_SUBSCRIPTION,
            },
            { silentFetch: true },
        );
    };

    useRunCallbackIfValueDeeplyChanged([userData, page, size, sort], () => {
        triggerWebhookSubscriptionsFetch();
        triggerWebhookSubscriptionsActionsFetch();
    });

    const [createAction, filteredActions] = extractCreateEntityAction(webhookSubscriptionActions?.data);

    return (
        <>
            <Head>
                <title>Webhook Subscriptions</title>
            </Head>
            <PageInformationCard
                iconProps={{ flip: "horizontal" }}
                icon={getWebhookNotificationsPageData().icon}
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        Webhook Subscriptions
                    </div>
                }
                content={
                    <>
                        <div>
                            <div>Here you can create, view and test your webhook subscriptions</div>
                            <br />
                            <ExternalLink href={"https://developer.ingrid.com/returns/public-api/webhooks"}>Webhooks Documentation</ExternalLink>
                        </div>
                    </>
                }
            />
            <TurnrCard>
                <TurnrCardContent>
                    <TurnrGrid container spacing={2} alignItems={"center"}>
                        {createAction && (
                            <TurnrGrid item>
                                <EntityActionButton
                                    appProps={appProps}
                                    entityAction={createAction}
                                    onSuccess={triggerWebhookSubscriptionsFetch}
                                    buttonProps={{
                                        variant: "outlined",
                                        size: "medium",
                                    }}
                                />
                            </TurnrGrid>
                        )}
                        <TurnrGrid item>
                            <EntityActionsDotsMenu hideIfEmpty={true} appProps={appProps} entityType={ENTITY_TYPE.WEBHOOK_SUBSCRIPTION} entityActions={filteredActions} />
                        </TurnrGrid>
                    </TurnrGrid>
                </TurnrCardContent>
                <TurnrTableSmart cellSize="small" tableHeadData={tableHeadData} tableSortData={tableSortData} serviceData={webhookSubscriptions} page={page} setPage={setPage} pageSize={size}>
                    {webhookSubscriptions?.data?.content?.map((subscription) => {
                        return (
                            <TurnrTableRow
                                sx={{ padding: "10px" }}
                                // onClick={() => openNotificationModal(notification)}
                                key={subscription?.id}
                            >
                                {!selectedBrandName && (
                                    <TurnrTableCell className={styles["mobile-hide"]}>
                                        <a
                                            onClick={() => {
                                                setSelectedBrandId(subscription?.brand?.id);
                                            }}
                                        >
                                            {subscription?.merchantBrand?.name}
                                        </a>
                                    </TurnrTableCell>
                                )}
                                <TurnrTableCell>{subscription?.type}</TurnrTableCell>
                                <TurnrTableCell>{subscription?.status}</TurnrTableCell>
                                <TurnrTableCell>{formatReadableDateAndTime({ dateTime: subscription?.createdAt })}</TurnrTableCell>
                                {/*TODO use EntityActionsDotsMenu somehow*/}
                                <TableCellActions>
                                    <EntityActionsDotsMenu appProps={appProps} entity={subscription} />
                                </TableCellActions>
                            </TurnrTableRow>
                        );
                    })}
                </TurnrTableSmart>
            </TurnrCard>
        </>
    );
}
